import React, { useState, useEffect } from "react";
import {
  AppBar,
  Typography,
  Button,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Tooltip,
  Grid,
  TextField,
  Alert,
  Card,
  CardContent,
  Avatar,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { externalgetApi } from "../../services/externalApis.tsx";
import { formatYNDisplay } from "../../services/itemImportStaging.ts";
import { ITEM_API_01 } from "../../api/ApiModel.tsx";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import AppDialog from "../../components/common/AppDialog.tsx";

// Types and Interfaces
interface User {
  USER_PARAM?: {
    COMPCODE?: string;
    STAFFMAPLOCODE?: string;
    USERNAME?: string;
  };
}

interface RootState {
  user: User;
}

interface LocationState {
  selectedItem?: ApiItem;
  itemData?: ApiItem;
}

interface ApiItem {
  itemcode?: string | number;
  itemname?: string;
  variantdata?: string;
  endate?: string;
  uptype?: string;
  sts?: string;
  modelname?: string;
  brandcode?: string | number;
  maincatcode?: string | number;
  catcode?: string | number;
  subcatcode?: string | number;
  genericcode?: string | number;
  packagecode?: string | number;
  measuringunitcode?: string | number;
  packageunits?: number;
  descrip?: string;
  isselling?: string | null;
  isinv?: string | null;
  imagetype?: string;
  maincatname?: string;
  catname?: string;
  subcatname?: string;
  genericname?: string;
  brandname?: string;
  enuser?: string;
  docpathjson?: string;
  measuringunit?: string;
  packagename?: string;
  [key: string]: any;
}

interface ImageData {
  id: number;
  filename: string;
  filepath: string;
  doctypecode: string;
  doctypeothername: string;
  remark: string;
  imageUrl: string;
  FILEPATH?: string;
  bytarr?: string;
  DOCTYPECODE?: string;
  DOCTYPEOTHERNAME?: string;
  REMARK?: string;
}

interface VariantDisplay {
  type: string;
  values: string[];
}

interface GridVariantRow {
  id: number;
  variantname: string;
  values: string[];
}

interface VariantDataItem {
  variantcode?: number | string;
  variantname?: string;
  variantName?: string;
  variantvalue?: string;
  variantValue?: string;
}

// Extended theme interface
interface CustomTheme {
  palette: {
    greyscale?: {
      stroke_1?: string;
      icons?: string;
    };
    background: {
      bgShade_1?: string;
      paper: string;
      default: string;
    };
    text: {
      primary: string;
      secondary: string;
    };
  };
  typography: {
    body_s?: {
      fontSize: string;
    };
    button_s?: {
      fontWeight: string;
      fontSize: string;
    };
    h6?: {
      fontWeight: string;
      fontSize: string;
    };
  };
}

function ViewItemData(): React.ReactElement {
  const theme = useTheme() as CustomTheme;
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();

  // Get user from Redux store
  const SYS_USER: User = useSelector((state: RootState) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE } = SYS_USER.USER_PARAM || {};

  // Get item data from navigation state
  const locationState = location.state as LocationState || {};
  const { selectedItem, itemData } = locationState;
  const item: ApiItem = selectedItem || itemData || {};

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error" | "warning" | "info">("success");
  const [alertMessage, setAlertMessage] = useState<string>("");

  // State for images
  const [itemImages, setItemImages] = useState<ImageData[]>([]);
  const [variants, setVariants] = useState<VariantDisplay[]>([]);
  const [imageType, setImageType] = useState<string>("common");
  const [openImageDialog, setOpenImageDialog] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<ImageData | null>(null);

  // Fetch item details on component mount
  useEffect(() => {
    if (item?.itemcode) {
      fetchItemDetails();
    }
    
    if (item?.variantdata) {
      parseVariantData(item.variantdata);
    }
    
    // Set imageType from item data
    if (item?.imagetype) {
      setImageType(item.imagetype);
    }
  }, [item]);

  const fetchItemDetails = async (): Promise<void> => {
    try {
      setIsLoading(true);

      const params = {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
        itemcode: item.itemcode,
      };

      const response = await externalgetApi(ITEM_API_01, "GET", params);

      // Normalize: API may return object { "85": {...}, "86": {...} } or array
      let itemsList: ApiItem[] = [];
      if (response && Array.isArray(response)) {
        itemsList = response;
      } else if (response && typeof response === "object" && !Array.isArray(response)) {
        itemsList = Object.values(response);
      }

      if (itemsList.length === 0) {
        setItemImages([]);
        return;
      }

      const itemCodeMatch = (data: ApiItem): boolean =>
        data.itemcode === item.itemcode ||
        data.itemcode === parseInt(String(item.itemcode), 10) ||
        String(data.itemcode) === String(item.itemcode);

      const itemDetails = itemsList.find(itemCodeMatch);

      if (itemDetails) {
        parseImageData(itemDetails.docpathjson);
        parseVariantData(itemDetails.variantdata);
        if (itemDetails.imagetype) {
          setImageType(itemDetails.imagetype);
        }
      } else {
        setItemImages([]);
      }
    } catch (error) {
      console.error("Error fetching item details:", error);
      setItemImages([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Add debugging
  useEffect(() => {
    console.log("Current item in ViewItemData:", item);
    console.log("Item Images state:", itemImages);
    console.log("Image Type:", imageType);
  }, [item, itemImages, imageType]);

  const parseImageData = (docpathjson?: string | null): void => {
    if (!docpathjson || docpathjson === "") {
      setItemImages([]);
      return;
    }

    try {
      let parsedData: any;

      if (typeof docpathjson === "string") {
        let cleaned = docpathjson.trim();
        cleaned = cleaned.replace(/\\"/g, '"');
        cleaned = cleaned.replace(/\\\\/g, "\\");
        if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
          cleaned = cleaned.slice(1, -1);
        }
        try {
          parsedData = JSON.parse(cleaned);
        } catch (e) {
          if (docpathjson.startsWith("[")) {
            parsedData = JSON.parse(docpathjson);
          } else if (docpathjson.startsWith("{")) {
            parsedData = [JSON.parse(docpathjson)];
          } else {
            setItemImages([]);
            return;
          }
        }
      } else {
        parsedData = docpathjson;
      }

      const arr = Array.isArray(parsedData) ? parsedData : parsedData ? [parsedData] : [];

      const formattedImages: ImageData[] = arr
        .map((img: any, index: number) => {
          let imageUrl = "";
          if (img.FILEPATH && (img.FILEPATH.startsWith("http") || img.FILEPATH.startsWith("data:"))) {
            imageUrl = img.FILEPATH;
          } else if (img.bytarr) {
            imageUrl = `data:image/jpeg;base64,${img.bytarr}`;
          }
          return {
            id: Date.now() + index,
            filename: img.filename || img.FILEPATH?.split("/").pop() || `image_${index + 1}.jpg`,
            filepath: img.FILEPATH || "",
            doctypecode: img.DOCTYPECODE || "IMG",
            doctypeothername: img.DOCTYPEOTHERNAME || "Image",
            remark: img.REMARK || "",
            imageUrl,
          };
        })
        .filter((img: ImageData) => img.imageUrl);

      setItemImages(formattedImages);
    } catch (error) {
      console.error("Error parsing image data:", error);
      setItemImages([]);
    }
  };

  const parseVariantData = (variantInput?: string | null): void => {
    if (variantInput === undefined || variantInput === null || variantInput === "") {
      setVariants([]);
      return;
    }

    try {
      const str = typeof variantInput === "string" ? variantInput.trim() : String(variantInput);

      // API format: JSON string array e.g. "[{\"variantcode\":1,\"variantname\":\"Size\",\"variantvalue\":\"3\"},...]"
      if (str.startsWith("[")) {
        let parsed: VariantDataItem[] = [];
        try {
          let cleaned = str.replace(/\\"/g, '"');
          if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
            cleaned = cleaned.slice(1, -1);
          }
          parsed = JSON.parse(cleaned);
        } catch (e) {
          parsed = JSON.parse(str);
        }
        const arr = Array.isArray(parsed) ? parsed : [];
        // Group by variantname, collect variantvalue
        const byName: Record<string, string[]> = {};
        arr.forEach((v: VariantDataItem) => {
          const name = (v.variantname || v.variantName || "").trim();
          const val = (v.variantvalue || v.variantValue || "").trim();
          if (!name) return;
          if (!byName[name]) byName[name] = [];
          if (val && !byName[name].includes(val)) byName[name].push(val);
        });
        const variantArray: VariantDisplay[] = Object.entries(byName).map(([type, values]) => ({ type, values }));
        setVariants(variantArray);
        return;
      }

      // Legacy format: "Type1: val1, val2; Type2: val3"
      const variantArray: VariantDisplay[] = [];
      const variantEntries = str.split(";").map((v) => v.trim()).filter(Boolean);
      variantEntries.forEach((entry) => {
        const [type, valuesStr] = entry.split(":").map((s) => s.trim());
        if (type && valuesStr) {
          const values = valuesStr.split(",").map((v) => v.trim()).filter(Boolean);
          variantArray.push({ type, values });
        }
      });
      setVariants(variantArray);
    } catch (error) {
      console.error("Error parsing variant data:", error);
      setVariants([]);
    }
  };

  const handleEdit = (): void => {
    navigate("/vCreateItem", {
      state: {
        selectedItem: item,
        actionText: "Edit",
        itemData: item
      }
    });
  };

  const handleBack = (): void => {
    navigate("/items");
  };

  const handleViewImage = (image: ImageData): void => {
    setSelectedImage(image);
    setOpenImageDialog(true);
  };

  const handleCloseImageDialog = (): void => {
    setOpenImageDialog(false);
    setSelectedImage(null);
  };

  // Function to get image URL
  const getImageUrl = (imageData: ImageData | null): string | null => {
    if (!imageData) return null;
    
    // Check if we have imageUrl
    if (imageData.imageUrl) {
      return imageData.imageUrl;
    }
    
    // Check if filepath contains a URL
    if (imageData.filepath && (imageData.filepath.startsWith('http') || imageData.filepath.startsWith('data:'))) {
      return imageData.filepath;
    }
    
    return null;
  };

  // Function to determine upload type display
  const getUploadTypeDisplay = (): string => {
    if (imageType === "common") {
      return "Common Images (Same for all variants)";
    } else if (imageType === "itemwise") {
      return "Item-wise Images (Different for each variant)";
    }
    return "Common Images";
  };

  return (
    <Box sx={styles.box_add_page_container}>
      {/* Alert System */}
      {alertOpen && (
        <Alert 
          severity={alertType} 
          onClose={() => setAlertOpen(false)}
          sx={{ 
            position: 'fixed', 
            top: 20, 
            right: 20, 
            zIndex: 9999,
            minWidth: 300 
          }}
        >
          {alertMessage}
        </Alert>
      )}

      <Box sx={styles.box_breadcrumb}>
        {/* Left side: Back button and breadcrumbs */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Tooltip title="Back to Item List" arrow>
            <IconButton
              sx={styles.btn_breadcrumb_back}
              onClick={handleBack}
            >
              <ArrowBackIcon sx={styles.btn_breadcrumb_back_icon} />
            </IconButton>
          </Tooltip>
          <Typography sx={styles.breadcrumb_divider} ml={1}>|</Typography>
          <Breadcrumbs separator="›" sx={{ ...styles.breadcrumb, ml: 1 }}>
            <Link underline="hover" sx={styles.breadcrumb_link} onClick={handleBack}>
              Home
            </Link>
            <Link underline="hover" sx={styles.breadcrumb_link} onClick={handleBack}>
              Item Management
            </Link>
            <Link underline="hover" sx={styles.breadcrumb_link} onClick={handleBack}>
              Item List
            </Link>
            <Typography sx={styles.breadcrumb_link_page}>
              View Item
            </Typography>
          </Breadcrumbs>
        </Box>
      </Box>

      <AppBar sx={styles.appbar}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Typography sx={styles.appbar_title}>
            View Item: {item.itemname || "Item"}
          </Typography>
        </Box>
      </AppBar>

      <Box
        component="form"
        noValidate
        autoComplete="off"
        sx={{
          ...styles.add_form_page_container,
        }}
      >
        {/* Main Content Grid */}
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
          Item Details
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Left Column */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Brand Name */}
              <Box>
                <TextField
                  label="Brand Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.brandname || "N/A"}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              </Box>

              {/* Item Category */}
              <Box>
                <TextField
                  label="Item Category"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.catname || "N/A"}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              </Box>

              {/* Generic Name */}
              <Box>
                <TextField
                  label="Generic name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.genericname || "N/A"}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              </Box>

              {/* Unit Of Measure */}
              <Box>
                <TextField
                  label="Unit Of Measure"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.measuringunit || "N/A"}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              </Box>

              {/* Model Name */}
              <Box>
                <TextField
                  label="Model Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.modelname || "N/A"}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Item Main Category */}
              <Box>
                <TextField
                  label="Item Main Category"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.maincatname || "N/A"}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              </Box>

              {/* Item Sub Category */}
              <Box>
                <TextField
                  label="Item Subcategory"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.subcatname || "N/A"}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              </Box>

              {/* Item Name/Product Name */}
              <Box>
                <TextField
                  label="Item Name/ Product Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.itemname || "N/A"}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              </Box>

              {/* Packaging Type */}
              <Box>
                <TextField
                  label="Packaging Type"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.packagename || "N/A"}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              </Box>

              {/* Unit Per Package */}
              <Box>
                <TextField
                  label="Unit Per Package"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.packageunits?.toString() || "0"}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              </Box>

              {/* Item Type */}
              <Box>
                <TextField
                  label="Item Type"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={(() => {
                    const selling = formatYNDisplay(item.isselling ?? item.ISSELLING);
                    const inventory = formatYNDisplay(item.isinv ?? item.ISINV);
                    const parts: string[] = [];
                    if (selling) parts.push(`Selling: ${selling}`);
                    if (inventory) parts.push(`Inventory: ${inventory}`);
                    return parts.length > 0 ? parts.join(", ") : "N/A";
                  })()}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              </Box>

              {/* Image Upload Type */}
              {/* <Box>
                <TextField
                  label="Image Upload Type"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={getUploadTypeDisplay()}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              </Box> */}
            </Box>
          </Grid>
        </Grid>

        {/* Description */}
        <Box sx={{ mb: 4 }}>
          <TextField
            label="Description"
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={3}
            value={item.descrip || "N/A"}
            InputProps={{
              readOnly: true,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '& .MuiInputBase-input': {
                color: 'text.primary',
              }
            }}
          />
        </Box>

        {/* Variant Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
            Variants
          </Typography>
          
          {variants.length > 0 ? (
            <Box
              sx={{
                "& .MuiPaper-root > .MuiBox-root": {
                  height: "240px !important",
                },
              }}
            >
              <ViewDataGrid
                columns={[
                  {
                    field: "variantname",
                    headerName: "Variant Name",
                    width: 200,
                    flex: 1,
                    minWidth: 150,
                  },
                  {
                    field: "values",
                    headerName: "Values",
                    flex: 2,
                    minWidth: 200,
                    renderCell: (params: any) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 1 }}>
                        {params.value.map((value: string, index: number) => (
                          <Chip
                            key={index}
                            label={value}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.875rem',
                              height: 24,
                              borderColor: '#4d80ee',
                              color: '#4d80ee',
                            }}
                          />
                        ))}
                      </Box>
                    ),
                  },
                ]}
                rows={variants.map((variant: VariantDisplay, index: number): GridVariantRow => ({
                  id: index,
                  variantname: variant.type,
                  values: variant.values,
                }))}
                menuItems={[]}
                tabletitle="Variants"
                gridid="id"
                displayActions={false}
                displaycheckbox={false}
                processRowUpdate={(newRow: any) => newRow}
              />
            </Box>
          ) : (
            <Box
              sx={{
                p: 2,
                border: `1px dashed #E0E0E0`,
                borderRadius: 1,
                backgroundColor: '#F5F5F5',
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
                No variants defined for this item.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Images Section */}
        <Box sx={{ 
          mb: 4, 
          p: 3, 
          border: '1px solid #4d4d4dff', 
          borderRadius: 1 
        }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Uploaded Images ({itemImages.length})
            </Typography>
          </Box>
          
          {/* Display Uploaded Images - Square 1:1 ratio with smaller size */}
          {itemImages.length > 0 ? (
            <Grid container spacing={2}>
              {itemImages.map((image: ImageData, index: number) => {
                const imageUrl = getImageUrl(image);
                return (
                  <Grid item key={image.id || index} xs={6} sm={4} md={3} lg={2}>
                    <Card
                      sx={{
                        cursor: "pointer",
                        border: `1px solid #E0E0E0`,
                        borderRadius: 1,
                        boxShadow: `0px 1px 2px rgba(0, 0, 0, 0.1)`,
                        "&:hover": {
                          boxShadow: `0px 4px 8px rgba(0, 0, 0, 0.2)`,
                          transform: "translateY(-2px)",
                          transition: "all 0.3s ease",
                        },
                      }}
                      onClick={() => handleViewImage(image)}
                    >
                      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                        {/* Square container with 1:1 aspect ratio - smaller size */}
                        <Box sx={{ 
                          position: 'relative',
                          width: '100%',
                          paddingBottom: '100%', // Creates 1:1 aspect ratio
                          overflow: 'hidden',
                          borderRadius: '4px'
                        }}>
                          <Avatar
                            src={imageUrl || undefined}
                            variant="square"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover', // This ensures the image covers the square area
                            }}
                          >
                            {!imageUrl && <ImageIcon sx={{ fontSize: 30, color: '#9E9E9E' }} />}
                          </Avatar>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 4,
                border: `1px dashed #E0E0E0`,
                borderRadius: 1,
                backgroundColor: "rgba(0, 0, 0, 0.02)",
              }}
            >
              <ImageIcon sx={{ fontSize: 60, color: '#9E9E9E' }} />
            </Box>
          )}
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: 2,
        mt: 3,
        px: 3,
        pb: 3
      }}>
        <Button
          variant="outlined"
          sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
          onClick={handleBack}
          disabled={isLoading}
        >
          Back
        </Button>
        <Button
          variant="contained"
          sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
          onClick={handleEdit}
          disabled={isLoading}
        >
          Edit
        </Button>
      </Box>

      {/* Image Preview Dialog */}
      <AppDialog
        open={openImageDialog}
        onClose={handleCloseImageDialog}
        title="Image Preview"
        maxWidth="md"
        fullWidth
        cancelLabel="Close"
        onCancel={handleCloseImageDialog}
      >
        {selectedImage && (
          <Box sx={{ width: "100%", maxWidth: 600, mx: "auto" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#F5F5F5",
                borderRadius: "4px",
                p: 2,
                minHeight: 100,
              }}
            >
              <img
                src={getImageUrl(selectedImage) || ""}
                alt={selectedImage.filename || "Image preview"}
                style={{
                  maxWidth: "100%",
                  maxHeight: 500,
                  objectFit: "contain",
                  borderRadius: "4px",
                }}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                {selectedImage.filename}
              </Typography>
              {selectedImage.filepath && (
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    mt: 1,
                    cursor: "pointer",
                    textDecoration: "underline",
                    "&:hover": {
                      textDecoration: "none",
                    },
                  }}
                  onClick={() => window.open(selectedImage.filepath, "_blank")}
                >
                  View Full Image
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </AppDialog>
    </Box>
  );
}

export default ViewItemData;